<template>
  <div class="sun-compass" :style="compassStyle">
    <svg
      ref="svgRef"
      :viewBox="`0 0 ${SIZE} ${SIZE}`"
      class="sun-compass__svg"
      @pointermove="onMove"
      @pointerup="endDrag"
      @pointerleave="endDrag">
      <!-- Track ring -->
      <circle :cx="CX" :cy="CY" :r="R" class="sun-compass__track" />

      <!-- Cardinal ticks + labels -->
      <g v-for="cardinal in cardinals" :key="cardinal.label">
        <line
          class="sun-compass__tick"
          :x1="bearingToPoint(cardinal.bearing, R).x"
          :y1="bearingToPoint(cardinal.bearing, R).y"
          :x2="bearingToPoint(cardinal.bearing, R + 9).x"
          :y2="bearingToPoint(cardinal.bearing, R + 9).y" />
        <text
          class="sun-compass__tick-label"
          :x="bearingToPoint(cardinal.bearing, R + 24).x"
          :y="bearingToPoint(cardinal.bearing, R + 24).y"
          text-anchor="middle"
          dominant-baseline="central">
          {{ cardinal.label }}
        </text>
      </g>

      <!-- Azimuth window sector (full circle when min==max or wraps 360°) -->
      <path v-if="windowPath" :d="windowPath.d" class="sun-compass__window" />
      <circle v-else :cx="CX" :cy="CY" :r="R" class="sun-compass__window-full" />

      <!-- Facade direction hint (center of window) -->
      <line class="sun-compass__facade" :x1="CX" :y1="CY" :x2="facadePoint.x" :y2="facadePoint.y" />

      <!-- Window edge handles (draggable) -->
      <circle
        class="sun-compass__handle"
        :cx="minPoint.x"
        :cy="minPoint.y"
        :r="8"
        @pointerdown="startDrag('min', $event)" />
      <circle
        class="sun-compass__handle sun-compass__handle--max"
        :cx="maxPoint.x"
        :cy="maxPoint.y"
        :r="8"
        @pointerdown="startDrag('max', $event)" />

      <!-- Live sun position -->
      <template v-if="sunPoint">
        <circle class="sun-compass__sun-halo" :cx="sunPoint.x" :cy="sunPoint.y" :r="11" />
        <circle class="sun-compass__sun" :cx="sunPoint.x" :cy="sunPoint.y" :r="6" />
      </template>
    </svg>

    <!-- Readouts -->
    <div class="sun-compass__readout">
      <span class="sun-compass__value">{{ min }}°</span>
      <span class="sun-compass__arrow">⇄</span>
      <span class="sun-compass__value">{{ max }}°</span>
      <span v-if="sunAzimuth != null" class="sun-compass__sun-now"> ☀ {{ Math.round(sunAzimuth) }}° </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    /** Window start azimuth (0-360, compass bearing) */
    min: number;
    /** Window end azimuth (0-360, compass bearing) */
    max: number;
    /** Live sun azimuth for the indicator dot (optional) */
    sunAzimuth?: number | null;
  }>(),
  {
    sunAzimuth: null,
  },
);

const emit = defineEmits<{
  (e: 'update:min', value: number): void;
  (e: 'update:max', value: number): void;
}>();

const SIZE = 240;
const CX = 120;
const CY = 120;
const R = 92;

const svgRef = ref<SVGSVGElement | null>(null);
const dragging = ref<'min' | 'max' | null>(null);

const compassStyle = {
  '--sun-compass-track': 'rgba(148, 163, 184, 0.25)',
  '--sun-compass-window': 'rgba(245, 158, 11, 0.32)',
  '--sun-compass-window-stroke': 'rgb(245, 158, 11)',
  '--sun-compass-handle': 'rgb(245, 158, 11)',
  '--sun-compass-facade': 'rgba(148, 163, 184, 0.6)',
};

const cardinals = [
  { label: 'N', bearing: 0 },
  { label: 'E', bearing: 90 },
  { label: 'S', bearing: 180 },
  { label: 'W', bearing: 270 },
];

function bearingToPoint(bearing: number, r: number): { x: number; y: number } {
  const rad = (bearing * Math.PI) / 180;
  return { x: CX + r * Math.sin(rad), y: CY - r * Math.cos(rad) };
}

function eventToBearing(event: PointerEvent): number {
  const rect = svgRef.value?.getBoundingClientRect();
  if (!rect || rect.width === 0) return 0;
  const svgX = ((event.clientX - rect.left) / rect.width) * SIZE;
  const svgY = ((event.clientY - rect.top) / rect.height) * SIZE;
  const dx = svgX - CX;
  const dy = svgY - CY;
  return ((((Math.atan2(dx, -dy) * 180) / Math.PI) % 360) + 360) % 360;
}

function startDrag(which: 'min' | 'max', event: PointerEvent) {
  dragging.value = which;
  (event.currentTarget as Element).setPointerCapture?.(event.pointerId);
}

function onMove(event: PointerEvent) {
  if (!dragging.value) return;
  const bearing = eventToBearing(event);
  const rounded = Math.round(bearing);
  if (dragging.value === 'min') emit('update:min', rounded);
  else emit('update:max', rounded);
}

function endDrag() {
  dragging.value = null;
}

const windowPath = computed(() => {
  const span = (props.max - props.min + 360) % 360 || 360;
  if (span >= 359.5) return null;
  const p1 = bearingToPoint(props.min, R);
  const p2 = bearingToPoint(props.max, R);
  const largeArc = span > 180 ? 1 : 0;
  return {
    d: `M ${CX} ${CY} L ${p1.x} ${p1.y} A ${R} ${R} 0 ${largeArc} 1 ${p2.x} ${p2.y} Z`,
  };
});

const minPoint = computed(() => bearingToPoint(props.min, R));
const maxPoint = computed(() => bearingToPoint(props.max, R));

const sunPoint = computed(() => (props.sunAzimuth == null ? null : bearingToPoint(props.sunAzimuth, R - 8)));

/** Center of the window — the direction the facade faces. */
const facadePoint = computed(() => {
  const mid = (props.min + props.max) / 2 + (props.min > props.max ? 180 : 0);
  return bearingToPoint(((mid % 360) + 360) % 360, R - 26);
});
</script>

<style scoped>
.sun-compass {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.sun-compass__svg {
  width: 100%;
  max-width: 240px;
  touch-action: none;
  user-select: none;
  overflow: visible;
}

.sun-compass__track {
  fill: rgba(148, 163, 184, 0.06);
  stroke: var(--sun-compass-track);
  stroke-width: 1.5;
}

.sun-compass__tick {
  stroke: var(--sun-compass-track);
  stroke-width: 1.5;
}

.sun-compass__tick-label {
  fill: rgba(100, 116, 139, 0.9);
  font-size: 11px;
  font-weight: 600;
}

.sun-compass__window {
  fill: var(--sun-compass-window);
  stroke: var(--sun-compass-window-stroke);
  stroke-width: 1.5;
}

.sun-compass__window-full {
  fill: var(--sun-compass-window);
  stroke: var(--sun-compass-window-stroke);
  stroke-width: 1.5;
}

.sun-compass__facade {
  stroke: var(--sun-compass-facade);
  stroke-width: 2;
  stroke-dasharray: 3 4;
  pointer-events: none;
}

.sun-compass__handle {
  fill: var(--sun-compass-handle);
  stroke: #fff;
  stroke-width: 2;
  cursor: grab;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.25));
}

.sun-compass__handle--max {
  fill: #f97316;
}

.sun-compass__handle:active {
  cursor: grabbing;
}

.sun-compass__sun-halo {
  fill: rgba(250, 204, 21, 0.28);
}

.sun-compass__sun {
  fill: #facc15;
  stroke: #b45309;
  stroke-width: 1;
}

.sun-compass__readout {
  display: flex;
  align-items: center;
  gap: 8px;
  font-variant-numeric: tabular-nums;
}

.sun-compass__value {
  font-size: 12px;
  font-weight: 700;
  color: rgb(245, 158, 11);
}

.sun-compass__arrow {
  color: rgba(100, 116, 139, 0.7);
  font-size: 12px;
}

.sun-compass__sun-now {
  margin-left: 6px;
  font-size: 11px;
  color: rgba(100, 116, 139, 0.9);
  background: rgba(250, 204, 21, 0.12);
  border-radius: 999px;
  padding: 2px 8px;
}
</style>
